import Drawer from '@/components/Drawer';
import {
  fetchEmailTemplatesRequest,
  renderEmailTemplatePreviewRequest,
} from '@/services/api/email';
import {
  EmailTemplateListItem,
  RenderedTemplate,
} from '@/typings/models/Email';
import { F } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import {
  Empty,
  Input,
  List,
  Skeleton,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import styles from './EmailTemplateSelector.less';

const { Text } = Typography;
const { Search } = Input;

interface EmailTemplateSelectorProps {
  open: boolean;
  leadId?: string;
  onClose: () => void;
  /** Receives the server-rendered subject + HTML (renderEmail path). */
  onSelect: (
    rendered: RenderedTemplate,
    template: EmailTemplateListItem,
  ) => void;
}

/**
 * "Insert template": picks an Email Center template and renders it through the
 * server's renderEmail() with the lead's context — the same path a real send
 * uses, so what lands in the composer is what a recipient would get.
 */
const EmailTemplateSelector: React.FC<EmailTemplateSelectorProps> = ({
  open,
  leadId,
  onClose,
  onSelect,
}) => {
  const intl = useIntl();
  const [templates, setTemplates] = useState<EmailTemplateListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [rendering, setRendering] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchEmailTemplatesRequest().then((Response: any) => {
      processAPIResponseNow(
        Response,
        {
          onSuccess(response: any) {
            setTemplates(response?.data || response || []);
          },
          onEnd() {
            setLoading(false);
          },
        },
        'data',
      );
    });
  }, [open]);

  const filtered = useMemo(
    () =>
      templates.filter((template) =>
        template.name?.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [templates, searchQuery],
  );

  const handleSelect = (template: EmailTemplateListItem) => {
    setRendering(template.uuid);
    renderEmailTemplatePreviewRequest(template.uuid, {
      lead_uuid: leadId,
    }).then((Response: any) => {
      processAPIResponseNow(
        Response,
        {
          onSuccess(response: any) {
            onSelect(
              { subject: response.subject, html: response.html },
              template,
            );
            onClose();
          },
          onError(error: any) {
            message.error(error?.message);
          },
          onEnd() {
            setRendering(null);
          },
        },
        'data',
      );
    });
  };

  return (
    <Drawer
      title={<F id="email.templates.insert" defaultMessage="Insert template" />}
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      width={420}
    >
      <Search
        placeholder={intl.formatMessage({
          id: 'email.templates.search',
          defaultMessage: 'Search templates',
        })}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className={styles.search}
      />
      {loading ? (
        <div>
          {[0, 1, 2, 3].map((row) => (
            <Skeleton
              key={row}
              active
              title={{ width: '45%' }}
              paragraph={{ rows: 1, width: '70%' }}
              className={styles.skeletonRow}
            />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <List
          dataSource={filtered}
          rowKey="uuid"
          renderItem={(template) => (
            <List.Item
              className={styles.templateItem}
              onClick={() => rendering === null && handleSelect(template)}
            >
              <List.Item.Meta
                title={
                  <span>
                    {template.name}{' '}
                    {template.is_public && !template.is_owned && (
                      <Tag>
                        <F
                          id="email.templates.master"
                          defaultMessage="Master"
                        />
                      </Tag>
                    )}
                  </span>
                }
                description={<Text type="secondary">{template.subject}</Text>}
              />
              {rendering === template.uuid && <Spin size="small" />}
            </List.Item>
          )}
        />
      )}
    </Drawer>
  );
};

export default EmailTemplateSelector;
